import type { ReactNode } from "react";

/**
 * Help Center content — account dashboard › Help.
 *
 * A flat list of topic categories (the sub-menu); each holds a handful of
 * FAQ entries rendered as accordions. Copy is realistic placeholder text
 * aligned with the project docs (multi-currency, PayPal/Russia/crypto rails,
 * 1 Rubyn point = 1 USD, retail/dropship fulfilment, VAT not live yet) — it
 * reads like the real thing but is meant to be reviewed before launch.
 *
 * The copy itself lives in `messages/<locale>.json` under `help.*`; this file
 * only keeps the structure — ordering, ids and icons.
 *
 * TODO(backend): once the support/CMS lands, source these from an endpoint
 * (per-locale, pre-translated) instead of this static catalogue.
 */

export type FaqEntry = {
  /** stable id for the accordion (used in keys + aria wiring). */
  id: string;
  questionId: string;
  answerId: string;
};

export type HelpCategory = {
  key: string;
  titleId: string;
  /** one-line teaser shown on the category card. */
  blurbId: string;
  icon: ReactNode;
  faqs: FaqEntry[];
};

/** Stroke icon helper — matches the design-system icon style (currentColor). */
const ico = (children: ReactNode): ReactNode => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {children}
  </svg>
);

export const HELP_CATEGORIES: HelpCategory[] = [
  {
    key: "orders",
    titleId: "help.orders.title",
    blurbId: "help.orders.blurb",
    icon: ico(
      <>
        <path d="M5 7.5 12 4l7 3.5v9L12 20l-7-3.5v-9Z" />
        <path d="m5 7.5 7 3.5 7-3.5M12 11v9" />
      </>,
    ),
    faqs: [
      {
        id: "where-is-order",
        questionId: "help.orders.faq.where-is-order.q",
        answerId: "help.orders.faq.where-is-order.a",
      },
      {
        id: "change-cancel",
        questionId: "help.orders.faq.change-cancel.q",
        answerId: "help.orders.faq.change-cancel.a",
      },
      {
        id: "missing-items",
        questionId: "help.orders.faq.missing-items.q",
        answerId: "help.orders.faq.missing-items.a",
      },
      {
        id: "order-confirmation",
        questionId: "help.orders.faq.order-confirmation.q",
        answerId: "help.orders.faq.order-confirmation.a",
      },
    ],
  },
  {
    key: "shipping",
    titleId: "help.shipping.title",
    blurbId: "help.shipping.blurb",
    icon: ico(
      <>
        <path d="M3 7h11v8H3zM14 10h4l3 3v2h-7z" />
        <circle cx="7" cy="17" r="1.6" />
        <circle cx="17.5" cy="17" r="1.6" />
      </>,
    ),
    faqs: [
      {
        id: "delivery-times",
        questionId: "help.shipping.faq.delivery-times.q",
        answerId: "help.shipping.faq.delivery-times.a",
      },
      {
        id: "shipping-cost",
        questionId: "help.shipping.faq.shipping-cost.q",
        answerId: "help.shipping.faq.shipping-cost.a",
      },
      {
        id: "where-ship",
        questionId: "help.shipping.faq.where-ship.q",
        answerId: "help.shipping.faq.where-ship.a",
      },
      {
        id: "customs",
        questionId: "help.shipping.faq.customs.q",
        answerId: "help.shipping.faq.customs.a",
      },
    ],
  },
  {
    key: "returns",
    titleId: "help.returns.title",
    blurbId: "help.returns.blurb",
    icon: ico(
      <>
        <path d="M9 7H6V4" />
        <path d="M6.5 7A7 7 0 1 1 5 12" />
      </>,
    ),
    faqs: [
      {
        id: "return-window",
        questionId: "help.returns.faq.return-window.q",
        answerId: "help.returns.faq.return-window.a",
      },
      {
        id: "how-to-return",
        questionId: "help.returns.faq.how-to-return.q",
        answerId: "help.returns.faq.how-to-return.a",
      },
      {
        id: "refund-time",
        questionId: "help.returns.faq.refund-time.q",
        answerId: "help.returns.faq.refund-time.a",
      },
      {
        id: "faulty",
        questionId: "help.returns.faq.faulty.q",
        answerId: "help.returns.faq.faulty.a",
      },
    ],
  },
  {
    key: "payments",
    titleId: "help.payments.title",
    blurbId: "help.payments.blurb",
    icon: ico(
      <>
        <rect x="3" y="6" width="18" height="12" rx="2" />
        <path d="M3 10h18M7 15h4" />
      </>,
    ),
    faqs: [
      {
        id: "methods",
        questionId: "help.payments.faq.methods.q",
        answerId: "help.payments.faq.methods.a",
      },
      {
        id: "currencies",
        questionId: "help.payments.faq.currencies.q",
        answerId: "help.payments.faq.currencies.a",
      },
      {
        id: "vat",
        questionId: "help.payments.faq.vat.q",
        answerId: "help.payments.faq.vat.a",
      },
      {
        id: "discount-codes",
        questionId: "help.payments.faq.discount-codes.q",
        answerId: "help.payments.faq.discount-codes.a",
      },
      {
        id: "secure",
        questionId: "help.payments.faq.secure.q",
        answerId: "help.payments.faq.secure.a",
      },
    ],
  },
  {
    key: "account",
    titleId: "help.account.title",
    blurbId: "help.account.blurb",
    icon: ico(
      <>
        <path d="M12 3 5 6v5c0 4 3 6.5 7 8 4-1.5 7-4 7-8V6l-7-3Z" />
        <path d="m9 12 2 2 4-4" />
      </>,
    ),
    faqs: [
      {
        id: "update-info",
        questionId: "help.account.faq.update-info.q",
        answerId: "help.account.faq.update-info.a",
      },
      {
        id: "reset-password",
        questionId: "help.account.faq.reset-password.q",
        answerId: "help.account.faq.reset-password.a",
      },
      {
        id: "delete-account",
        questionId: "help.account.faq.delete-account.q",
        answerId: "help.account.faq.delete-account.a",
      },
      {
        id: "notifications",
        questionId: "help.account.faq.notifications.q",
        answerId: "help.account.faq.notifications.a",
      },
    ],
  },
  {
    key: "affiliates",
    titleId: "help.affiliates.title",
    blurbId: "help.affiliates.blurb",
    icon: ico(
      <>
        <circle cx="9" cy="8" r="3" />
        <path d="M3.5 19a5.5 5.5 0 0 1 11 0" />
        <path d="M16 8.5a3 3 0 0 1 0 5M18 19a5.5 5.5 0 0 0-3-4.9" />
      </>,
    ),
    faqs: [
      {
        id: "what-points",
        questionId: "help.affiliates.faq.what-points.q",
        answerId: "help.affiliates.faq.what-points.a",
      },
      {
        id: "find-code",
        questionId: "help.affiliates.faq.find-code.q",
        answerId: "help.affiliates.faq.find-code.a",
      },
      {
        id: "official-affiliate",
        questionId: "help.affiliates.faq.official-affiliate.q",
        answerId: "help.affiliates.faq.official-affiliate.a",
      },
      {
        id: "redeem",
        questionId: "help.affiliates.faq.redeem.q",
        answerId: "help.affiliates.faq.redeem.a",
      },
    ],
  },
];
